<template>
  <div
    :class="[
      'container-subtitle-content',
      { 'background-black': chatPreferenceStore.subtitlePreference.subtitleBackgroundColor === 'BLACK' },
    ]"
    @dblclick="openSetting"
  >
    <div>
      <video ref="videoElement" autoplay playsinline />
      <div class="panel" :class="{ reverse: subtitleSettingPosition === 'bottom' }">
        <div :class="subtitleSettingPosition === 'top' ? 'pane-top' : 'pane-bottom'">
          <SubtitleSettings v-if="isVisibleSetting" @close="closeSetting" />
        </div>
        <div :class="subtitleSettingPosition === 'top' ? 'pane-bottom' : 'pane-top'">
          <SubtitleMessageText
            :messageList="messageList"
            :position="subtitleSettingPosition === 'top' ? 'bottom' : 'top'"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { provide, ref, watch, computed } from 'vue';

import { storeToRefs } from 'pinia';

import SubtitleMessageText from '@/components/domains/chat/chat-subtitle/SubtitleMessageText.vue';
import SubtitleSettings from '@/components/domains/chat/chat-subtitle/SubtitleSettings.vue';
import { useMessagesSubtitle } from '@/composables/domains/chat/useMessagesSubtitle';
import { useCamera } from '@/composables/shared/useCamera';
import { useChatPreferenceStore } from '@/stores/chatPreference';
import { SocketState, useSocketStore } from '@/stores/socket';
import { ChatMessage } from '@/types/socket';
import { ij_resetSubtitle } from '@/utils/injectionKeys';

const prevMyMessageId = ref(''); //최근 내 말풍선
const messageList = ref<Array<ChatMessage>>([]);
const settingMaxWidth = ref(1800); // 자막 너비
const settingMaxWidthLimit = ref(0); // 자막 너비
const isVisibleSetting = ref<boolean>(true); // 자막 설정 창 보임 여부

const { videoElement } = useCamera();
const { messageMap, handleMessageWebSocket } = useMessagesSubtitle({
  messageList,
  prevMyMessageId,
});
const socketStore = useSocketStore();
const chatPreferenceStore = useChatPreferenceStore();

const { socket, socketState } = storeToRefs(socketStore);

const subtitleSettingPosition = computed<'top' | 'bottom'>(() => {
  return chatPreferenceStore.subtitlePreference.subtitleIsSettingAbove ? 'top' : 'bottom';
});

const openSetting = () => {
  isVisibleSetting.value = true;
};

const closeSetting = () => {
  isVisibleSetting.value = false;
};

const onClickResetSubtitle = () => {
  messageMap.value = {} as ChatMessage;
  messageList.value = [];
};

// 화면 너비에 따라 자막 너비 설정 최댓값 갱신
const setMaxWidthLimit = () => {
  settingMaxWidthLimit.value =
    window.innerWidth - parseFloat(getComputedStyle(document.documentElement).fontSize) * 1.5;

  if (settingMaxWidth.value > settingMaxWidthLimit.value) {
    settingMaxWidth.value = settingMaxWidthLimit.value;
  }
};

onMounted(() => {
  setMaxWidthLimit();
  window.addEventListener('resize', setMaxWidthLimit);
});

onUnmounted(() => {
  window.removeEventListener('resize', setMaxWidthLimit);
});

watch(
  () => socketState.value,
  (state) => {
    if (SocketState.isOpen(state)) {
      socket.value!.onmessage = (event) => {
        handleMessageWebSocket(event);
      };
    }
  },
  { immediate: true }
);

provide(ij_resetSubtitle, onClickResetSubtitle);
</script>

<style scoped lang="scss">
.container-subtitle-content {
  width: 100%;
  height: 100dvh;
  background-color: var(--background-subtitle);
  display: flex;
}

.background-black {
  background-color: #222222;
}

.mic-input-container {
  position: absolute;
  bottom: 0;
  left: 0;
  margin-top: 0;
  width: 100%;
  display: flex;
  justify-content: center;
  z-index: 999;
}

.icon-top {
  top: 1rem;
  margin-left: 1rem;
  cursor: pointer;
}

.icon-setting {
  position: absolute;
  top: 1rem;
  right: 1rem;
  cursor: pointer;
}

video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  object-fit: cover;
  z-index: 0;
}

.panel {
  display: flex;
  flex-direction: column;

  &.reverse {
    flex-direction: column-reverse;
  }

  .pane-top {
    max-height: 500px;
    height: 50vh;
    width: 100vw;
    position: relative;
  }

  .pane-bottom {
    width: 100vw;
    height: 50vh;
    max-height: 500px;
    display: flex;
    align-items: flex-end;
  }
}
</style>
